import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useApp } from '../context/AppContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ThemeToggle from './ThemeToggle';

const { FiSettings, FiHome, FiBuilding, FiBarChart3, FiLogOut, FiUser } = FiIcons;

function AdminLayout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useApp();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navigation = [
    { name: 'Overview', href: '/admin', icon: FiHome },
    { name: 'Prisons', href: '/admin/prisons', icon: FiBuilding },
    { name: 'Reports', href: '/admin/reports', icon: FiBarChart3 }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-government-50 to-primary-50 dark:from-government-900 dark:to-government-800 transition-colors duration-300">
      {/* Header */}
      <header className="bg-white/80 dark:bg-government-900/80 backdrop-blur-sm border-b border-government-200 dark:border-government-700 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Link to="/admin" className="flex items-center space-x-3">
              <div className="bg-red-600 p-2 rounded-lg">
                <SafeIcon icon={FiSettings} className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-government-900 dark:text-white">AB Orders</h1>
                <p className="text-xs text-government-600 dark:text-government-300">System Administration</p>
              </div>
            </Link>

            <nav className="hidden md:flex items-center space-x-8">
              {navigation.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-lg font-medium transition-colors ${
                      isActive
                        ? 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900'
                        : 'text-government-600 dark:text-government-300 hover:text-government-900 dark:hover:text-white hover:bg-government-50 dark:hover:bg-government-800'
                    }`}
                  >
                    <SafeIcon icon={item.icon} className="w-5 h-5" />
                    <span>{item.name}</span>
                  </Link>
                );
              })}
            </nav>

            <div className="flex items-center space-x-4">
              <ThemeToggle />
              <div className="flex items-center space-x-2 text-government-600 dark:text-government-300">
                <SafeIcon icon={FiUser} className="w-5 h-5" />
                <span className="hidden sm:inline font-medium">{user?.name}</span>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 text-government-600 dark:text-government-300 hover:text-red-600 dark:hover:text-red-400 transition-colors"
              >
                <SafeIcon icon={FiLogOut} className="w-5 h-5" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Navigation */}
      <div className="md:hidden bg-white dark:bg-government-900 border-b border-government-200 dark:border-government-700">
        <nav className="flex justify-around py-2">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`flex flex-col items-center space-y-1 px-3 py-2 rounded-lg transition-colors ${
                  isActive
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-government-600 dark:text-government-300'
                }`}
              >
                <SafeIcon icon={item.icon} className="w-5 h-5" />
                <span className="text-xs">{item.name}</span>
              </Link>
            );
          })}
        </nav>
      </div>

      {/* Main Content */}
      <main className="py-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {children}
        </motion.div>
      </main>
    </div>
  );
}

export default AdminLayout;